import { DEFAULT_COUNTRY, normalizeRegion, workspaceSettingsSchema, type WorkspaceSettings } from '@buildbee/contracts';
import { canadianSalesTax, defaultTaxRateBpsFor } from '../common/address';

export * from './templates';
export * from './trades';
export * from './automations';

const RESERVED_SLUGS = new Set([
  'www',
  'app',
  'api',
  'admin',
  'portal',
  'auth',
  'mail',
  'static',
  'assets',
  'cdn',
  'status',
  'help',
  'support',
  'docs',
  'blog',
  'billing',
  'webhooks',
  'buildbee',
]);

/** Turn a company name into a subdomain-safe slug (3-48 chars). */
export function slugify(input: string): string {
  const base = input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  if (base.length >= 3) return base;
  return `${base || 'workspace'}-co`.slice(0, 48);
}

export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.has(slug);
}

/** Candidate slugs to try in order when the preferred one is taken. */
export function slugCandidates(preferred: string, attempts = 5): string[] {
  const base = slugify(preferred);
  const out = isReservedSlug(base) ? [] : [base];
  for (let i = 2; out.length < attempts; i++) out.push(`${base}-${i}`);
  return out;
}

/** Company code typed at sign-in: 3-12 uppercase letters and digits (e.g. VANTOM). */
export const COMPANY_CODE_PATTERN = /^[A-Z0-9]{3,12}$/;

/** What the person typed, as stored: spaces and dashes dropped, uppercase. Null when it cannot be a code. */
export function normalizeCompanyCode(input: string | null | undefined): string | null {
  const code = (input ?? '').replace(/[\s-]+/g, '').toUpperCase();
  return COMPANY_CODE_PATTERN.test(code) ? code : null;
}

/** Candidate codes for a new company, in order (VANTOM, VANTOM2, VANTOM3, ...). */
export function companyCodeCandidates(name: string, attempts = 6): string[] {
  let base = slugify(name).replace(/-/g, '').toUpperCase().slice(0, 10);
  if (base.length < 3) base = `${base}CO`.slice(0, 10);
  const out = [base];
  for (let i = 2; out.length < attempts; i++) out.push(`${base}${i}`);
  return out;
}

/** Fill defaults for workspace settings (used at creation and when reading partial JSON). */
export function resolveSettings(partial: Partial<WorkspaceSettings> | null | undefined): WorkspaceSettings {
  return workspaceSettingsSchema.parse(partial ?? {});
}

/**
 * Settings that follow from where the business is: country, the province/state as `taxRegion`
 * and, for a Canadian province, its combined sales tax as `defaultTaxRateBps` (Ontario: 1300).
 * Currency, locale and timezone keep the Canadian defaults. US states get no default rate (US
 * sales tax is set locally).
 */
export function locationSettings(location: { country?: string | null; region?: string | null }): Partial<WorkspaceSettings> {
  const country = (location.country || DEFAULT_COUNTRY).toUpperCase();
  const taxRegion = normalizeRegion(country, location.region);
  const out: Partial<WorkspaceSettings> = { country };
  if (!taxRegion) return out;
  out.taxRegion = taxRegion;
  const rate = country === 'CA' ? defaultTaxRateBpsFor(taxRegion) : null;
  if (rate !== null) out.defaultTaxRateBps = rate;
  return out;
}

/** Tax components (GST/HST/PST/RST/QST) for a workspace's `taxRegion`; null outside Canada or when no province is set. */
export function workspaceTaxComponents(settings: Pick<WorkspaceSettings, 'country' | 'taxRegion'>, onDate?: string) {
  if (settings.country !== 'CA' || !settings.taxRegion) return null;
  return canadianSalesTax(settings.taxRegion, onDate);
}

export const INVITATION_TTL_DAYS = 14;

/** Extract the workspace slug from a host like `acme.buildbee.app` given the app's root domain. */
export function slugFromHost(host: string | null | undefined, rootDomain: string): string | null {
  if (!host) return null;
  const hostname = host.split(':')[0]!.toLowerCase();
  const root = rootDomain.toLowerCase().replace(/^\./, '');
  if (!hostname.endsWith(`.${root}`)) return null;
  const sub = hostname.slice(0, -(root.length + 1));
  if (!sub || sub.includes('.') || isReservedSlug(sub)) return null;
  return sub;
}
